import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./context";
import { getUserData, pingUserSession } from "./service";
import { handle401Error } from "@/lib/utils";

type ProtectedRouteProps = { children: React.ReactNode };

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const [isChecked, setIsChecked] = useState(false);
  const { user, setUser, resetUser } = useAuth();

  const pingQuery = useQuery({
    queryKey: ["auth", "ping"],
    queryFn: pingUserSession,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const shouldFetchUser = useMemo(
    () => pingQuery.data === true && !user,
    [pingQuery.data, user],
  );

  const userQuery = useQuery({
    queryKey: ["auth", "me"],
    queryFn: getUserData,
    enabled: shouldFetchUser,
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!pingQuery.isSuccess) return;
    if (!pingQuery.data) {
      resetUser();
      handle401Error("fetch");
    }
  }, [pingQuery.isSuccess, pingQuery.data, resetUser]);

  useEffect(() => {
    if (!shouldFetchUser) return;
    if (!userQuery.isSuccess) return;
    if (!userQuery.data) {
      resetUser();
      handle401Error("fetch");
      return;
    }
    setUser(userQuery.data);
  }, [
    resetUser,
    setUser,
    shouldFetchUser,
    userQuery.isSuccess,
    userQuery.data,
  ]);

  useEffect(() => {
    if (!pingQuery.isSuccess) return;
    if (!pingQuery.data) return;
    if (shouldFetchUser && !userQuery.isSuccess) return;
    if (shouldFetchUser && !userQuery.data) return;
    setIsChecked(true);
  }, [
    pingQuery.isSuccess,
    pingQuery.data,
    shouldFetchUser,
    userQuery.isSuccess,
    userQuery.data,
  ]);

  return isChecked && children;
};

export default ProtectedRoute;
